<template>
  <div :class="{ faded: isJobRemoved }" class="job rounded">
    <div class="row">
      <div class="col-sm-8 col-md-9 p-l-15 !tw-pr-[65px]">
        <!-- Title -->
        <div class="clip-text f-16 f-500">
          <component :is="isJobCreated ? 'a' : 'span'" :href="`/job/${jobId}`">
            {{ jobName ? jobName : 'Job without title' }}
          </component>
          <div v-if="companyName" class="f-13 f-500 c-gray">
            {{ companyName }}
          </div>
        </div>
        <div
          v-if="jobDates && jobHires && jobHires.data.length === 0"
          class="f-13 f-500 text-muted"
        >
          {{ jobDates }}
        </div>
        <template v-if="jobHires && jobHires.data.length">
          <div
            v-for="(hire, index) in jobHires.data"
            :key="`freelancer-${index}`"
            class="p-t-5 f-13 f-500"
          >
            <component
              :is="hire.freelancer.primaryProfile ? 'a' : 'span'"
              :href="hire.freelancer.primaryProfile ? hire.freelancer.primaryProfile.url : ''"
            >
              {{ hire.freelancer.name }}
            </component>
            <span class="text-muted m-l-5">{{ formatHireDates(hire) }}</span>
          </div>
        </template>
        <div class="p-t-5">
          <!-- Tags -->
          <span
            v-for="(tag, index) in tags"
            :key="hash(JSON.stringify(tag) + index)"
            class="m-r-5 m-b-5"
            style="display: inline-block"
          >
            <component :is="spanOrTooltip(tag)" :text="$t(tag.tooltip)">
              <WdsLabel sm :[tag.color]="true" :subtle="tag.color === 'primary'">
                <component :is="aTagOrSpan(tag)" :href="tag.url">
                  {{ tag.count }} {{ tag.count >= 0 ? $tc(tag.title, tag.count) : $t(tag.title) }}
                </component>
              </WdsLabel>
            </component>
          </span>
          <br />
          <!-- Labels-->
          <WdsLabel
            v-for="(label, index) in labels"
            :key="`label-${index}`"
            class="m-b-5 m-l-5"
            sm
            subtle
            :[label.color]="true"
          >
            {{ $t(label.text) }}
          </WdsLabel>
        </div>
      </div>

      <!-- Uses JobDropdown unless given another button in the slot -->
      <div class="col-sm-4 col-md-3 button-container" :class="{ 'p-0': shouldLinkToHire }">
        <slot name="button">
          <JobDropdown
            v-if="!shouldLinkToHire"
            :job-id="jobId"
            :is-job-open="isJobOpen"
            :is-job-approved="isJobApproved"
            :is-job-completed="isJobCompleted"
            :job-hires="!!jobHires"
            :has-job-hires="!!jobHiresAmount"
            :has-job-dialogs="!!jobDialogAmount"
            :is-job-available="isJobAvailable"
            :current-job-step="currentJobStep"
            :is-job-removed="isJobRemoved"
            :is-job-created="isJobCreated"
            :is-job-paused="isJobPaused"
            :button-link="buttonLink"
            :button-label="buttonLabel"
            :show-custom-fields="showCustomFields"
            :shared-with-recruiters="sharedWithRecruiters"
            :shared-with-trusted-contacts="sharedWithTrustedContacts"
            class="pull-right"
          />
          <WdsButton v-else sm :href="hireLink">{{ $t('Manage contract') }}</WdsButton>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { WdsButton, WdsLabel, WdsTooltip } from '@worksome/wds-components'
import BaseLabel from '@/components/BaseLabel.vue'
import HashMixin from '@/mixins/MethodHash'
import JobDropdown from '@/components/JobDropdown.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import BaseButton from '@/components/BaseButton.vue'

export default {
  name: 'JobsCardItem',
  components: { WdsButton, BaseButton, JobDropdown, BaseLabel, WdsLabel, WdsTooltip },
  mixins: [HashMixin],
  props: {
    jobId: {
      type: Number,
      required: true,
    },
    isJobOpen: {
      type: Boolean,
    },
    isJobCompleted: {
      type: Boolean,
    },
    isJobAvailable: {
      type: Boolean,
    },
    currentJobStep: {
      type: String,
      default: '0',
    },
    isJobRemoved: {
      type: Boolean,
      required: true,
    },
    isJobCreated: {
      type: Boolean,
      required: true,
    },
    isJobPublic: {
      type: Boolean,
      required: true,
    },
    isJobForMarketplace: {
      type: Boolean,
    },
    isJobPublished: {
      type: Boolean,
      required: true,
    },
    isJobApproved: {
      type: Boolean,
    },
    approvedBills: {
      type: Number,
      default: null,
    },
    unapprovedBills: {
      type: Number,
      default: null,
    },
    unpaidInvoices: {
      type: Number,
      default: null,
    },
    jobDialogAmount: {
      type: Number,
      default: 0,
    },
    jobBillsAmount: {
      type: Number,
      default: null,
    },
    jobHires: {
      type: Object,
      default: () => {},
    },
    jobHiresAmount: {
      type: Number,
      default: 0,
    },
    jobName: {
      type: String,
      required: true,
    },
    isJobPaused: {
      type: Boolean,
      required: true,
    },
    showCustomFields: {
      type: Boolean,
    },
    jobDates: {
      type: String,
      default: null,
    },
    buttonLink: {
      type: String,
      default: null,
    },
    buttonLabel: {
      type: String,
      default: null,
    },
    unansweredDialogs: {
      type: Number,
      default: null,
    },
    freshDialogs: {
      type: Number,
      default: null,
    },
    closedDialogs: {
      type: Number,
      default: null,
    },
    shortlistedDialogs: {
      type: Number,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    companyName: {
      type: String,
      default: null,
    },
    unpaidBillsCount: {
      type: Number,
      default: 0,
    },
    totalBills: {
      type: Number,
      default: null,
    },
    sharedWithTrustedContacts: {
      type: Boolean,
      default: false,
    },
    sharedWithRecruiters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownExpanded: false,
    }
  },
  computed: {
    tags() {
      let tags = []
      switch (this.type) {
        case 'DASHBOARD_HIRES':
          if (this.totalBills > 0) {
            tags.push({
              color: this.unpaidBillsCount > 0 ? 'warning' : 'success',
              title: this.totalBills > 1 ? 'bills' : 'bill',
              count: this.totalBills,
              tooltip:
                this.unpaidBillsCount > 0
                  ? 'Some bills are open for payment'
                  : 'All bills are paid',
            })
          } else {
            tags.push({
              color: 'gray',
              title: 'No bills',
            })
          }
          break
        case 'UNFILLED':
          tags = [
            {
              color: 'gray',
              title: 'new',
              count: this.freshDialogs,
              url: `/job/${this.jobId}/conversation-and-bids?type=fresh&orderBy=relevance`,
            },
            {
              color: 'gray',
              title: 'shortlisted',
              count: this.shortlistedDialogs,
              url: `/job/${this.jobId}/conversation-and-bids?type=shortlisted&orderBy=relevance`,
            },
            {
              color: 'gray',
              title: 'ended',
              count: this.closedDialogs,
              url: `/job/${this.jobId}/conversation-and-bids?type=closed&orderBy=relevance`,
            },
          ]
          if (this.isJobPaused) {
            tags.push({
              color: 'warning',
              title: 'Paused',
              tooltip: 'New bids has been paused for this job post',
            })
          }
          tags.push({
            color: 'primary',
            title: 'unanswered',
            count: this.unansweredDialogs,
            url: `/job/${this.jobId}/conversation-and-bids?type=unanswered&orderBy=relevance`,
          })
          break
        case 'FILLED':
          if (this.jobHiresAmount > 0) {
            tags.push({
              color: 'success',
              title: 'hire',
              count: this.jobHiresAmount,
            })
          }
          if (this.jobBillsAmount > 0) {
            tags.push({
              color: 'default',
              title: 'bill in total',
              count: this.jobBillsAmount,
            })
          }
          if (this.unapprovedBills > 0) {
            tags.push({
              color: 'warning',
              title: 'unapproved bill',
              count: this.unapprovedBills,
            })
          }
          if (this.unpaidInvoices > 0) {
            tags.push({
              color: 'warning',
              title: 'unpaid invoice',
              count: this.unpaidInvoices,
            })
          }
          break
        default:
          tags = []
          break
      }

      return tags
    },
    labels() {
      const labels = []
      if (!this.isJobCreated) {
        labels.push({
          color: 'error',
          text: 'Job post incomplete',
        })
      }
      if (this.isJobRemoved) {
        labels.push({
          color: 'error',
          text: 'Removed',
        })
      }
      if (this.type === 'UNFILLED' && !this.isJobPublished) {
        labels.push({
          color: 'gray',
          text: 'Not published and therefore hidden for everyone.',
        })
      }
      if (this.type === 'UNFILLED' && !this.isJobPublic) {
        labels.push({
          color: 'gray',
          text: 'Job visible to invitees only',
        })
      }
      if (this.isJobCreated && !this.isJobApproved && this.isJobForMarketplace) {
        labels.push({
          color: 'warning',
          text: 'Awaiting marketplace approval',
        })
      }
      return labels
    },
    shouldLinkToHire() {
      return this.jobHires?.data?.length > 0
    },
    hireLink() {
      if (this.shouldLinkToHire) {
        if (this.jobHires.data.length === 1) {
          return laravelNamedRoute('messages.show', { id: this.jobHires.data[0].threadId })
        }
        return laravelNamedRoute('jobs.show.contracts', { job: this.jobId })
      }
      return ''
    },
  },
  methods: {
    formatHireDates(hire) {
      const dateStart = moment(hire.dateStart).format('MMM D YYYY')

      const dateEnd = hire.dateEnd
        ? moment(hire.dateEnd).format('MMM D YYYY')
        : this.$t('No end date')

      return `${dateStart} - ${dateEnd}`
    },
    spanOrTooltip(tag) {
      return tag.tooltip ? 'WdsTooltip' : 'span'
    },
    aTagOrSpan(tag) {
      return tag.url ? 'a' : 'span'
    },
  },
}
</script>

<style lang="scss" scoped>
.job {
  padding: 12px 13px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: hsl(0deg 0% 99%) !important;
  }
}

// Hack to break onto a newline after three tags
a:nth-child(3)::after {
  content: '\A';
  white-space: pre;
}

.button-container {
  position: static;
}

:deep(.wds-label a) {
  color: inherit;
}
</style>
